import { object, string } from 'yup'
import { useSnackbar } from 'stores/useSnackbar'
import { yupResolver } from '@hookform/resolvers/yup'
import { useForm, useWatch } from 'react-hook-form'
import { useMemo } from 'react'
import { ActivityButton } from 'components/ActivityButton'
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
} from '@mui/material'
import { FormFieldErrorMessage } from 'components/FormFieldErrorMessage'
import { isPresent } from '@jjvgaming/player-payback-library'
import type { Patron } from 'types/api'
import { grey } from '@mui/material/colors'
import { isNil } from 'lodash'
import { useDeletePatronState } from 'hooks/api/Patron/useDeletePatronState'

interface PatronStateOption {
  state?: {
    id?: number
    stateName?: string | null
  } | null
}

const FlagPatronAccountAsDeleteFromStateSchema = object({
  currentStateId: string().required('State is required'),
})

export const FlagPatronAccountAsDeleteFromStateModal = ({
  isModalOpen,
  toggleIsOpen,
  toggleModalIsOpen,
  patron,
  states,
}: {
  isModalOpen: boolean
  toggleModalIsOpen: () => void
  toggleIsOpen: () => void
  patron: Patron
  states?: PatronStateOption[]
}) => {
  const formMethods = useForm({
    resolver: yupResolver(FlagPatronAccountAsDeleteFromStateSchema),
  })

  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
  } = formMethods

  const currentStateId = useWatch({
    control,
    name: 'currentStateId',
    defaultValue: 'None',
  })

  const stateOptions = Array.isArray(states) ? states : []

  // Sort states alphabetically and exclude the patrons current state so they can't delete from it
  const sortedStates = useMemo(
    () =>
      stateOptions
        .filter(
          (state) => String(state.state?.id) !== String(patron.currentStateId)
        )
        .sort((a, b) =>
          (a.state?.stateName ?? 'None').localeCompare(
            b.state?.stateName ?? 'None'
          )
        ),
    [stateOptions, patron.currentStateId]
  )

  const setSnackbarMessage = useSnackbar((state) => state.setMessage)

  const useDeletePatronStateMutation = useDeletePatronState({
    onSuccess: () => {
      setSnackbarMessage(`Patron has been updated.`)
      toggleModalIsOpen()
    },
    onError: () => {
      setSnackbarMessage(`Error: Cannot save Patron`, 'error')
    },
  })

  const handleSubmitWrapper = handleSubmit((formData) => {
    if (isNil(formData.currentStateId)) {
      setSnackbarMessage(`Error: invalid state`, 'error')
      return
    }

    useDeletePatronStateMutation.mutate({
      patronId: patron.id,
      stateId: Number(formData.currentStateId),
    })
  })

  return (
    <Dialog
      open={isModalOpen}
      maxWidth={'xl'}
      fullWidth
      PaperProps={{ sx: { p: 0, maxWidth: '900px' } }}
      onClose={toggleIsOpen}
    >
      <form onSubmit={handleSubmitWrapper} noValidate>
        <DialogTitle bgcolor={grey[50]} sx={{ p: 0 }} className="border-b">
          <Box px={4} py={4}>
            <Typography variant="h3" fontWeight={'bold'}>
              Delete Patron From State
            </Typography>
            <Typography variant="body-1" color={'text.secondary'}>
              {`Patron Name: ${
                (patron.firstName ?? '') + ' ' + (patron.lastName ?? '')
              }`}
            </Typography>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ pt: 4, px: 4 }}>
          <Typography
            variant="body-1"
            className="text-secondary"
            paddingTop={'10px'}
            pb={'16px'}
          >
            <b>Are you sure you want to delete this patron from the state?</b>
          </Typography>

          <div>
            <Stack direction="column" gap={1}>
              <Stack direction="column">
                <InputLabel
                  sx={{ fontSize: '16px', fontWeight: '100' }}
                  required
                >
                  Delete from state
                </InputLabel>
              </Stack>
              <Stack direction="column">
                <Select
                  fullWidth
                  placeholder="State"
                  defaultValue={'None'} // "Select a State"
                  {...register('currentStateId')}
                  error={isPresent(errors.currentStateId)}
                >
                  <MenuItem value="None">
                    <em>Select a State</em>
                  </MenuItem>
                  {/* Populate states */}
                  {sortedStates?.map((state) => (
                    <MenuItem key={state.state?.id} value={state.state?.id}>
                      {state.state?.stateName}
                    </MenuItem>
                  ))}
                </Select>
                {errors.currentStateId?.message && (
                  <FormFieldErrorMessage
                    message={errors.currentStateId.message}
                  />
                )}
              </Stack>
            </Stack>
          </div>
        </DialogContent>
        <DialogActions
          sx={{ borderTop: `1px solid ${grey[300]}`, pt: 2, pb: 2, px: 4 }}
        >
          <Button onClick={toggleModalIsOpen}>Cancel</Button>
          <ActivityButton
            variant="contained"
            disabled={
              !sortedStates ||
              sortedStates.length === 0 ||
              currentStateId === 'None' ||
              !currentStateId
            }
            active={useDeletePatronStateMutation.isPending}
            onClick={handleSubmitWrapper}
          >
            Delete Patron From State
          </ActivityButton>
        </DialogActions>
      </form>
    </Dialog>
  )
}
